import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@marineform/environment';
import { EMPTY, filter, Observable, takeUntil } from 'rxjs';
import { MANAGE_API_PATHS } from '../data';

/**
 * True while manage is waiting on POST `/admin/auth/sign-out`.
 *
 * {@link LogoutGateInterceptor} drops new session-authenticated calls
 * (except sign-out) and `POST /session/refresh` while this is set. Reset
 * in `AuthService.logout` finalize, including timeout.
 */
export const manageLogoutInFlight = signal(false);

/**
 * Drops or aborts session-minting HTTP while manage logout is in flight.
 *
 * New gated calls return EMPTY so they never reach the network. In-flight
 * calls unsubscribe when the gate becomes true. Sign-out and `auth`
 * `/logout` pass through so they cannot be blocked by the same gate.
 */
export const LogoutGateInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  if (!isGatedDuringLogout(req)) {
    return next(req);
  }

  if (manageLogoutInFlight()) {
    return EMPTY;
  }

  return next(req).pipe(takeUntil(manageLogoutStarted$()));
};

function isGatedDuringLogout(req: HttpRequest<unknown>): boolean {
  if (isSignOutRequest(req.url)) {
    return false;
  }
  if (req.url.startsWith(environment.ultramarineBaseUrl)) {
    return true;
  }
  return (
    req.method === 'POST' &&
    req.url.startsWith(`${environment.authnBaseUrl}/session/refresh`)
  );
}

function isSignOutRequest(url: string): boolean {
  return url.startsWith(
    `${environment.ultramarineBaseUrl}/${MANAGE_API_PATHS.SIGNOUT}`,
  );
}

/**
 * Emits when {@link manageLogoutInFlight} becomes true so in-flight
 * session-authenticated requests unsubscribe before they can persist a
 * refreshed session cookie.
 *
 * Must be called in an injection context (this interceptor).
 */
function manageLogoutStarted$(): Observable<true> {
  return toObservable(manageLogoutInFlight).pipe(
    filter((inFlight): inFlight is true => inFlight),
  );
}
